import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { isSafeRelativePath } from "@/lib/auth/safeRedirect";
import { trackTikTokEvent, identifyTikTokUser } from "@/lib/tiktokTracking";
import { trackXAdsEvent, identifyXUser } from "@/lib/xAdsTracking";
import { supabase } from "@/integrations/supabase/client";
import { OtpInput } from "@/components/auth/OtpInput";
import { AuthLayout } from "@/redesign/layouts";
import { RdButton, RdInput } from "@/redesign/components";

/**
 * Linear-grade auth: single 400px card, hairline border, generous whitespace.
 * Drives the same `useAuth` hook as the legacy page — visual rebuild only.
 */
export default function AuthRedesign() {
  const {
    user,
    profile,
    signUp,
    signIn,
    loading,
    isAdmin,
    resendVerificationEmail,
    resetPassword,
  } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Default to Sign IN. Returning users who land on /auth without ?mode=
  // were being shown Sign UP first, then hitting "User already registered"
  // when they typed their existing credentials — easy to misread as a
  // password failure. Marketing CTAs that want signup pass ?mode=signup
  // (handled by the searchParams effect below).
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Email verification (post-signup) state
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpExpiry, setOtpExpiry] = useState(600);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Forgot-password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  const trackedRef = useRef(false);

  // Track ViewContent (parity with legacy). Empty dep array + ref guard
  // ensures we fire exactly ONCE per mount even if the user later toggles
  // between signup/signin (the toggle triggers a re-render but not a
  // remount). Previously the [isSignUp] dep would double-fire.
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    trackTikTokEvent("ViewContent", {
      properties: {
        content_name: "Auth Page",
        content_type: "signup_page",
        signup_mode: isSignUp ? "signup" : "signin",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL mode + email pre-fill
  useEffect(() => {
    const mode = searchParams.get("mode");
    if (mode === "signin") setIsSignUp(false);
    else if (mode === "signup") setIsSignUp(true);
    const emailParam = searchParams.get("email");
    if (emailParam) setEmail(decodeURIComponent(emailParam));
  }, [searchParams]);

  // Redirect after successful auth.
  //
  // We navigate DIRECTLY to the role-specific home (replace:true) instead of
  // routing through /dashboard. The /dashboard route only existed to read the
  // role and re-navigate, which created a three-step chain (/auth → /dashboard
  // → /business/dashboard). Each hop unmounts/remounts the EmailVerificationGuard
  // and triggers a fresh lazy chunk load — under network flakiness or a stale
  // post-deploy chunk, the second hop could hang on Suspense with no fallback
  // chrome (blank white screen). Going straight to the destination eliminates
  // the race entirely and matches what /dashboard would have done.
  // Post-auth redirect logic.
  //
  // Resolution order (highest priority first):
  //   1. sessionStorage 'returnAfterAuth' — legacy, set by JoinCampaignViaLink
  //      and any same-tab redirect-back caller.
  //   2. URL ?redirect=<path> — the safer pattern for external links (email
  //      invites, shared URLs, cross-tab email verification). sessionStorage
  //      does NOT survive: (a) clicking an email link in a fresh browser,
  //      (b) opening the email verification link in a different tab, or
  //      (c) mobile mail apps that force a new tab. The URL param does.
  //   3. Onboarding gate.
  //   4. Role-specific home.
  //
  // Both mechanisms are validated as SAME-ORIGIN relative paths only — we
  // never navigate to an absolute URL from an untrusted redirect param, to
  // prevent open-redirect abuse in phishing (e.g. ?redirect=https://evil).
  useEffect(() => {
    if (user && profile && !loading) {
      const sessionReturn = sessionStorage.getItem("returnAfterAuth");
      const urlReturn = searchParams.get("redirect");
      const candidate = sessionReturn || urlReturn;
      if (candidate && isSafeRelativePath(candidate)) {
        sessionStorage.removeItem("returnAfterAuth");
        navigate(candidate, { replace: true });
        return;
      }
      if (!profile.onboarding_completed) {
        // Preserve the redirect through onboarding: if the URL had one,
        // stash it in sessionStorage so OnboardingRedesign can pick it up
        // after the user finishes setup. (Onboarding redirects internally
        // and drops the URL param.)
        if (urlReturn && isSafeRelativePath(urlReturn)) {
          sessionStorage.setItem("returnAfterAuth", urlReturn);
        }
        navigate("/onboarding", { replace: true });
        return;
      }
      const target = resolveRoleHome(profile.role, {
        isAdmin,
        hasOrganization: Boolean(profile.organization_id),
      });
      navigate(target, { replace: true });
    }
  }, [user, profile, loading, isAdmin, navigate, searchParams]);

  // OTP expiry + resend cooldown
  useEffect(() => {
    if (!showVerificationPrompt || otpExpiry <= 0) return;
    const t = setTimeout(() => setOtpExpiry((p) => p - 1), 1000);
    return () => clearTimeout(t);
  }, [showVerificationPrompt, otpExpiry]);
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((p) => p - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Profile-fetch escape hatch
  const [profileLoadTimedOut, setProfileLoadTimedOut] = useState(false);
  useEffect(() => {
    if (!user || profile) {
      setProfileLoadTimedOut(false);
      return;
    }
    const t = setTimeout(() => setProfileLoadTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, [user, profile]);

  if (user && !profile) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-4 py-12 text-center">
          <Loader2 className="h-7 w-7 animate-spin text-[hsl(var(--rd-accent-500))]" />
          <p className="text-[13px] text-[hsl(var(--rd-text-2))]">Loading your account…</p>
          {profileLoadTimedOut && (
            <div className="flex flex-col items-center gap-2">
              <p className="max-w-sm text-[12.5px] text-[hsl(var(--rd-text-3))]">
                This is taking longer than expected.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className="text-[12.5px] text-[hsl(var(--rd-accent-500))] underline-offset-2 hover:underline"
                >
                  Reload
                </button>
                <button
                  onClick={async () => {
                    await supabase.auth.signOut();
                    window.location.href = "/auth";
                  }}
                  className="text-[12.5px] text-[hsl(var(--rd-accent-500))] underline-offset-2 hover:underline"
                >
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </AuthLayout>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (isSignUp) {
        if (password !== confirmPassword) {
          toast({
            title: "Passwords don't match",
            description: "Please make sure both passwords match.",
            variant: "destructive",
          });
          return;
        }
        const { error } = await signUp(email, password, {});
        if (error) {
          toast({ title: "Sign up failed", description: error.message, variant: "destructive" });
        } else {
          // TikTok SubmitForm — mid-funnel event (form submitted OK but
          // onboarding not yet complete). Non-blocking.
          try {
            await trackTikTokEvent("SubmitForm", {
              user: { email },
              properties: {
                content_type: "signup_submit",
                content_name: "Signup form submitted",
                signup_mode: "signup",
                source: "auth_page_redesign",
              },
            });
          } catch (trackErr) {
            console.warn("[TikTok] SubmitForm tracking failed (non-blocking):", trackErr);
          }
          try {
            await trackXAdsEvent("Signup", {
              user: { email },
              properties: {
                content_type: "signup_submit",
                content_name: "Signup form submitted",
              },
            });
          } catch (xErr) {
            console.warn("[X] Signup tracking failed (non-blocking):", xErr);
          }

          setPendingEmail(email);
          setShowVerificationPrompt(true);
          setOtpExpiry(600);
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          toast({ title: "Sign in failed", description: error.message, variant: "destructive" });
        } else {
          // Identify the pixel session with the user's email for stronger EMQ.
          try {
            identifyTikTokUser({ email });
            identifyXUser({ email });
          } catch (idErr) {
            console.warn("[TikTok/X] identify failed (non-blocking):", idErr);
          }
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) return;
    setVerifying(true);
    try {
      // Use 'email' type — works for both signup-confirmation and
      // magiclink/recovery OTPs (the edge function uses generateLink({ type: 'magiclink' }),
      // which produces a recovery-type OTP for already-confirmed users).
      // 'signup' would only validate fresh-signup confirmation tokens and reject everything else.
      const { error } = await supabase.auth.verifyOtp({
        email: pendingEmail,
        token: otpCode,
        type: "email",
      });
      if (error) {
        toast({ title: "Verification failed", description: error.message, variant: "destructive" });
      } else {
        setVerified(true);
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    const result = await resendVerificationEmail(pendingEmail);
    if (result.error) {
      toast({ title: "Resend failed", description: result.error.message, variant: "destructive" });
    } else {
      toast({ title: "Email sent", description: "Check your inbox for a new code." });
      setResendCooldown(60);
      setOtpExpiry(600);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { error } = await resetPassword(forgotPasswordEmail);
      if (error) {
        toast({ title: "Reset failed", description: error.message, variant: "destructive" });
      } else {
        setForgotPasswordSent(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Verification screen ------------------------------------------------------
  if (showVerificationPrompt) {
    if (verified) {
      return (
        <AuthLayout>
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--rd-success)/0.15)] text-[hsl(var(--rd-success))]">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <h1 className="text-[22px] font-semibold tracking-tight">Email verified</h1>
            <p className="text-[13.5px] text-[hsl(var(--rd-text-2))]">
              Redirecting you to onboarding…
            </p>
          </div>
        </AuthLayout>
      );
    }

    const mins = Math.floor(otpExpiry / 60);
    const secs = otpExpiry % 60;
    return (
      <AuthLayout>
        <button
          onClick={() => {
            setShowVerificationPrompt(false);
            setOtpCode("");
          }}
          className="mb-6 inline-flex items-center gap-1.5 text-[12.5px] text-[hsl(var(--rd-text-3))] hover:text-[hsl(var(--rd-text-1))]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[hsl(var(--rd-accent-500)/0.15)] text-[hsl(var(--rd-accent-500))]">
            <Mail className="h-5 w-5" />
          </div>
          <h1 className="text-[24px] font-semibold tracking-tight">Verify your email</h1>
          <p className="max-w-sm text-[13.5px] text-[hsl(var(--rd-text-2))]">
            We sent a 6-digit code to{" "}
            <span className="font-medium text-[hsl(var(--rd-text-1))]">{pendingEmail}</span>
          </p>
        </div>
        <div className="mt-8 flex flex-col items-center gap-4">
          <OtpInput value={otpCode} onChange={setOtpCode} />
          <div className="text-[12px] text-[hsl(var(--rd-text-3))]">
            Code expires in{" "}
            <span className="font-mono tabular-nums text-[hsl(var(--rd-text-2))]">
              {mins}:{secs.toString().padStart(2, "0")}
            </span>
          </div>
          <RdButton
            className="w-full"
            disabled={otpCode.length !== 6 || verifying}
            onClick={handleVerifyOtp}
          >
            {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify email"}
          </RdButton>
          <button
            disabled={resendCooldown > 0}
            onClick={handleResend}
            className="text-[12.5px] text-[hsl(var(--rd-accent-500))] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-[hsl(var(--rd-text-3))] disabled:no-underline"
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Forgot password screen ---------------------------------------------------
  if (showForgotPassword) {
    return (
      <AuthLayout>
        <button
          onClick={() => {
            setShowForgotPassword(false);
            setForgotPasswordSent(false);
          }}
          className="mb-6 inline-flex items-center gap-1.5 text-[12.5px] text-[hsl(var(--rd-text-3))] hover:text-[hsl(var(--rd-text-1))]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to sign in
        </button>
        {forgotPasswordSent ? (
          <div className="flex flex-col gap-4 text-center">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[hsl(var(--rd-success)/0.15)] text-[hsl(var(--rd-success))]">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <h1 className="text-[22px] font-semibold tracking-tight">Check your email</h1>
            <p className="text-[13.5px] text-[hsl(var(--rd-text-2))]">
              We sent a password reset link to{" "}
              <span className="font-medium text-[hsl(var(--rd-text-1))]">
                {forgotPasswordEmail}
              </span>
              .
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-[24px] font-semibold tracking-tight">Reset your password</h1>
            <p className="mt-2 text-[13.5px] text-[hsl(var(--rd-text-2))]">
              Enter your email and we'll send a reset link.
            </p>
            <form onSubmit={handleForgotPassword} className="mt-6 flex flex-col gap-4">
              <Field label="Email">
                <RdInput
                  type="email"
                  placeholder="you@company.com"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  required
                />
              </Field>
              <RdButton type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Send reset link"
                )}
              </RdButton>
            </form>
          </>
        )}
      </AuthLayout>
    );
  }

  // Default sign-in / sign-up screen ----------------------------------------
  return (
    <AuthLayout>
      {/* Tab toggle */}
      <div className="mb-6 inline-flex rounded-md border border-[hsl(var(--rd-border))] bg-[hsl(var(--rd-surface-2))] p-0.5">
        <button
          onClick={() => setIsSignUp(false)}
          className={`rounded px-3 py-1 text-[12.5px] font-medium transition-colors ${
            !isSignUp
              ? "bg-[hsl(var(--rd-surface-1))] text-[hsl(var(--rd-text-1))] shadow-sm"
              : "text-[hsl(var(--rd-text-3))] hover:text-[hsl(var(--rd-text-2))]"
          }`}
        >
          Sign in
        </button>
        <button
          onClick={() => setIsSignUp(true)}
          className={`rounded px-3 py-1 text-[12.5px] font-medium transition-colors ${
            isSignUp
              ? "bg-[hsl(var(--rd-surface-1))] text-[hsl(var(--rd-text-1))] shadow-sm"
              : "text-[hsl(var(--rd-text-3))] hover:text-[hsl(var(--rd-text-2))]"
          }`}
        >
          Sign up
        </button>
      </div>

      <h1 className="text-[26px] font-semibold tracking-tight">
        {isSignUp ? "Create your account" : "Welcome back"}
      </h1>
      <p className="mt-2 text-[13.5px] text-[hsl(var(--rd-text-2))]">
        {isSignUp
          ? "Spin up your first CPM campaign in 20 minutes."
          : "Sign in to continue to CreatorVault."}
      </p>

      <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
        <Field label="Email">
          <RdInput
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </Field>
        <Field label="Password">
          <RdInput
            type="password"
            placeholder={isSignUp ? "Create a password" : "Your password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={isSignUp ? "new-password" : "current-password"}
            required
            minLength={8}
          />
        </Field>
        {isSignUp && (
          <Field label="Confirm password">
            <RdInput
              type="password"
              placeholder="Repeat password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
            />
          </Field>
        )}
        {!isSignUp && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(true);
                setForgotPasswordEmail(email);
              }}
              className="text-[12.5px] text-[hsl(var(--rd-accent-500))] underline-offset-2 hover:underline"
            >
              Forgot password?
            </button>
          </div>
        )}
        <RdButton type="submit" disabled={isSubmitting || loading} className="w-full">
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isSignUp ? (
            "Create account"
          ) : (
            "Sign in"
          )}
        </RdButton>
      </form>

      <p className="mt-6 text-center text-[12px] text-[hsl(var(--rd-text-3))]">
        By continuing you agree to our{" "}
        <Link
          to="/terms-of-service"
          className="text-[hsl(var(--rd-text-2))] underline-offset-2 hover:underline"
        >
          Terms
        </Link>{" "}
        and{" "}
        <Link
          to="/privacy-policy"
          className="text-[hsl(var(--rd-text-2))] underline-offset-2 hover:underline"
        >
          Privacy Policy
        </Link>
        .
      </p>
    </AuthLayout>
  );
}

function resolveRoleHome(role: string | null | undefined): string {
  if (role === "business") return "/business/dashboard";
  if (role === "creator" || role === "demo") return "/creator/dashboard";
  // Unknown role — let /dashboard sort it out (admin check happens there).
  return "/dashboard";
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[12.5px] font-medium text-[hsl(var(--rd-text-2))]">{label}</span>
      {children}
    </label>
  );
}
